import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Clear, Delete, Refresh, Save, Search, Sync } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { biometricApi } from '../../../api/biometricApi';
import { employeeApi } from '../../../api/employeeApi';
import { Employee } from '../../../types';
import {
  BiometricAttendanceLogDto,
  BiometricDashboardDto,
  BiometricDeviceDto,
  EmployeeBiometricMapDto,
  SaveBiometricDeviceRequest,
  SaveEmployeeBiometricMapRequest,
} from '../../../types/biometricTypes';

const IP_ADDRESS_PATTERN = /^(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)){3}$/;
const BIOMAX_DEVICE_TYPES = ['BIOMAX', 'BIOMAX_SMARTOFFICE', 'SMARTOFFICE'];

type BiomaxConfigForm = {
  serialNumbers: string;
  initialWindowDays: number;
  overlapDays: number;
  deviceUtcOffsetMinutes: number | '';
};

const emptyDeviceForm: SaveBiometricDeviceRequest = {
  deviceName: '',
  deviceType: '',
  pollingInterval: 15,
  syncStrategy: 'PULL',
  ipAddress: '',
  port: null,
  apiUrl: '',
  apiKey: '',
  providerConfigJson: '',
  isActive: true,
};

const emptyBiomaxConfig: BiomaxConfigForm = {
  serialNumbers: '',
  initialWindowDays: 2,
  overlapDays: 1,
  deviceUtcOffsetMinutes: 330,
};

const emptyMappingForm: SaveEmployeeBiometricMapRequest = {
  employeeId: '',
  biometricDeviceId: '',
  deviceUserId: '',
  isActive: true,
};

const BiometricManagementPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'devices' | 'mappings' | 'logs'>('devices');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dashboard, setDashboard] = useState<BiometricDashboardDto | null>(null);
  const [devices, setDevices] = useState<BiometricDeviceDto[]>([]);
  const [mappings, setMappings] = useState<EmployeeBiometricMapDto[]>([]);
  const [logs, setLogs] = useState<BiometricAttendanceLogDto[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [deviceDialogOpen, setDeviceDialogOpen] = useState(false);
  const [mappingDialogOpen, setMappingDialogOpen] = useState(false);
  const [editingDevice, setEditingDevice] = useState<BiometricDeviceDto | null>(null);
  const [editingMapping, setEditingMapping] = useState<EmployeeBiometricMapDto | null>(null);
  const [deviceForm, setDeviceForm] = useState<SaveBiometricDeviceRequest>(emptyDeviceForm);
  const [biomaxConfig, setBiomaxConfig] = useState<BiomaxConfigForm>(emptyBiomaxConfig);
  const [mappingForm, setMappingForm] = useState<SaveEmployeeBiometricMapRequest>(emptyMappingForm);
  const [logFilter, setLogFilter] = useState({ biometricDeviceId: '', startDate: '', endDate: '', searchTerm: '' });
  const [deviceSearch, setDeviceSearch] = useState('');
  const [mappingSearch, setMappingSearch] = useState('');
  const [appliedDeviceSearch, setAppliedDeviceSearch] = useState('');
  const [appliedMappingSearch, setAppliedMappingSearch] = useState('');
  const [appliedLogFilter, setAppliedLogFilter] = useState({ biometricDeviceId: '', startDate: '', endDate: '', searchTerm: '' });
  const [searchRequestId, setSearchRequestId] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [dashboardData, deviceData, mappingData, logData, employeeData] = await Promise.all([
        biometricApi.getDashboard(),
        biometricApi.getDevices(appliedDeviceSearch || undefined),
        biometricApi.getMappings(undefined, appliedMappingSearch || undefined),
        biometricApi.getLogs(appliedLogFilter.biometricDeviceId || appliedLogFilter.startDate || appliedLogFilter.endDate || appliedLogFilter.searchTerm ? {
          biometricDeviceId: appliedLogFilter.biometricDeviceId || undefined,
          startDate: appliedLogFilter.startDate || undefined,
          endDate: appliedLogFilter.endDate || undefined,
          searchTerm: appliedLogFilter.searchTerm || undefined,
        } : undefined),
        employeeApi.getAll(1, 200),
      ]);

      setDashboard(dashboardData);
      setDevices(deviceData);
      setMappings(mappingData);
      setLogs(logData);
      setEmployees(employeeData);
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Failed to load biometric module data';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [appliedDeviceSearch, appliedLogFilter, appliedMappingSearch]);

  const isBiomaxDeviceType = (deviceType?: string) => BIOMAX_DEVICE_TYPES.includes((deviceType || '').trim().toUpperCase());
  const requiresTcpEndpoint = (deviceType?: string) => ['SDK', 'ESSL', 'ZKTECO'].includes((deviceType || '').trim().toUpperCase());
  const selectedDeviceType = (deviceForm.deviceType || '').trim().toUpperCase();
  const isBiomaxSelected = isBiomaxDeviceType(selectedDeviceType);
  const requiresTcpSelected = requiresTcpEndpoint(selectedDeviceType);

  useEffect(() => {
    void loadData();
  }, [loadData, searchRequestId]);

  const requestSearch = () => {
    setSearchRequestId((value) => value + 1);
  };

  const handleDeviceSearch = () => {
    setAppliedDeviceSearch(deviceSearch.trim());
    requestSearch();
  };

  const handleClearDeviceSearch = () => {
    setDeviceSearch('');
    setAppliedDeviceSearch('');
    requestSearch();
  };

  const handleMappingSearch = () => {
    setAppliedMappingSearch(mappingSearch.trim());
    requestSearch();
  };

  const handleClearMappingSearch = () => {
    setMappingSearch('');
    setAppliedMappingSearch('');
    requestSearch();
  };

  const handleLogSearch = () => {
    setAppliedLogFilter({
      biometricDeviceId: logFilter.biometricDeviceId,
      startDate: logFilter.startDate,
      endDate: logFilter.endDate,
      searchTerm: logFilter.searchTerm.trim(),
    });
    requestSearch();
  };

  const handleClearLogSearch = () => {
    const emptyFilter = { biometricDeviceId: '', startDate: '', endDate: '', searchTerm: '' };
    setLogFilter(emptyFilter);
    setAppliedLogFilter(emptyFilter);
    requestSearch();
  };

  const validateDevice = (form: SaveBiometricDeviceRequest) => {
    if (!form.deviceName.trim()) return 'Device name is required';
    if (!form.deviceType.trim()) return 'Device type is required';

    const normalizedIpAddress = form.ipAddress?.trim() || '';
    if (requiresTcpEndpoint(form.deviceType)) {
      if (!normalizedIpAddress) return 'IP address is required';
      if (!IP_ADDRESS_PATTERN.test(normalizedIpAddress)) return 'Please enter a valid IP address';
      if (!form.port || form.port <= 0) return 'Port must be greater than 0';
    }

    if ((isBiomaxDeviceType(form.deviceType) || form.deviceType.trim().toUpperCase() === 'API') && !form.apiUrl?.trim()) {
      return 'API URL is required';
    }

    if (isBiomaxDeviceType(form.deviceType) && !form.apiKey?.trim() && !editingDevice) {
      return 'API key is required for BioMax SmartOffice';
    }

    if (!form.pollingInterval || form.pollingInterval <= 0) return 'Polling interval must be greater than 0';
    if (!form.syncStrategy.trim()) return 'Sync strategy is required';

    const isDuplicateDevice = requiresTcpEndpoint(form.deviceType)
      ? devices.some((device) =>
        device.id !== editingDevice?.id
        && (device.ipAddress || '').trim() === normalizedIpAddress
        && device.port === form.port
      )
      : false;

    if (isDuplicateDevice) {
      return 'Device already registered with same IP and Port';
    }

    return null;
  };

  const validateMapping = (form: SaveEmployeeBiometricMapRequest) => {
    if (!form.employeeId) return 'Employee is required';
    if (!form.biometricDeviceId) return 'Device is required';

    const normalizedCode = form.deviceUserId.trim();
    if (!normalizedCode) return 'Device user code is required';
    if (!/^\d+$/.test(normalizedCode)) return 'Device user code must be numeric';

    const isDuplicateMapping = mappings.some((mapping) =>
      mapping.id !== editingMapping?.id
      && mapping.employeeId === form.employeeId
      && mapping.biometricDeviceId === form.biometricDeviceId
    );

    if (isDuplicateMapping) {
      return 'Employee already mapped to this device';
    }

    const isDuplicateCode = mappings.some((mapping) =>
      mapping.id !== editingMapping?.id
      && mapping.biometricDeviceId === form.biometricDeviceId
      && mapping.deviceUserId.trim() === normalizedCode
    );

    if (isDuplicateCode) {
      return 'Device user code already exists for this device';
    }

    return null;
  };

  const openCreateDevice = () => {
    setEditingDevice(null);
    setDeviceForm(emptyDeviceForm);
    setBiomaxConfig(emptyBiomaxConfig);
    setDeviceDialogOpen(true);
  };

  const parseBiomaxConfig = (providerConfigJson?: string | null): BiomaxConfigForm => {
    if (!providerConfigJson) return emptyBiomaxConfig;
    try {
      const parsed = JSON.parse(providerConfigJson);
      return {
        serialNumbers: Array.isArray(parsed.serialNumbers) ? parsed.serialNumbers.join(', ') : (parsed.serialNumbers || ''),
        initialWindowDays: Number(parsed.initialWindowDays || parsed.dateWindowDays || 2),
        overlapDays: Number(parsed.overlapDays ?? 1),
        deviceUtcOffsetMinutes: parsed.deviceUtcOffsetMinutes ?? 330,
      };
    } catch {
      return emptyBiomaxConfig;
    }
  };

  const buildBiomaxProviderConfigJson = () => JSON.stringify({
    serialNumbers: biomaxConfig.serialNumbers
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean),
    initialWindowDays: Math.max(1, Number(biomaxConfig.initialWindowDays) || 2),
    overlapDays: Math.max(0, Number(biomaxConfig.overlapDays) || 0),
    deviceUtcOffsetMinutes: biomaxConfig.deviceUtcOffsetMinutes === '' ? undefined : Number(biomaxConfig.deviceUtcOffsetMinutes),
  });

  const openEditDevice = (device: BiometricDeviceDto) => {
    setEditingDevice(device);
    setDeviceForm({
      id: device.id,
      deviceName: device.deviceName,
      deviceType: device.deviceType,
      ipAddress: device.ipAddress || '',
      port: device.port,
      apiUrl: device.apiUrl || '',
      apiKey: '',
      sdkPath: device.sdkPath || '',
      providerConfigJson: device.providerConfigJson || '',
      pollingInterval: device.pollingInterval,
      syncStrategy: device.syncStrategy,
      isActive: device.isActive,
    });
    setBiomaxConfig(parseBiomaxConfig(device.providerConfigJson));
    setDeviceDialogOpen(true);
  };

  const openEditMapping = (mapping?: EmployeeBiometricMapDto) => {
    if (mapping) {
      setEditingMapping(mapping);
      setMappingForm({
        employeeId: mapping.employeeId,
        biometricDeviceId: mapping.biometricDeviceId || '',
        deviceUserId: mapping.deviceUserId,
        isActive: mapping.isActive,
      });
    } else {
      setEditingMapping(null);
      setMappingForm(emptyMappingForm);
    }
    setMappingDialogOpen(true);
  };

  const handleSaveDevice = async () => {
    const validation = validateDevice(deviceForm);
    if (validation) {
      toast.warning(validation);
      return;
    }

    setSaving(true);
    try {
      const payload: SaveBiometricDeviceRequest = {
        ...deviceForm,
        deviceType: deviceForm.deviceType.trim().toUpperCase(),
        providerConfigJson: isBiomaxDeviceType(deviceForm.deviceType) ? buildBiomaxProviderConfigJson() : deviceForm.providerConfigJson,
      };
      if (editingDevice) {
        await biometricApi.updateDevice(editingDevice.id, payload);
        toast.success('Biometric device updated');
      } else {
        await biometricApi.createDevice(payload);
        toast.success('Biometric device created');
      }
      setDeviceDialogOpen(false);
      await loadData();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to save biometric device');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDevice = async (id: string) => {
    setSaving(true);
    try {
      await biometricApi.deleteDevice(id);
      toast.success('Biometric device deleted');
      await loadData();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to delete biometric device');
    } finally {
      setSaving(false);
    }
  };

  const handleSyncDevice = async (id: string) => {
    setSaving(true);
    try {
      const result = await biometricApi.syncDevice(id);
      toast.success(`${result.message} Imported: ${result.imported}, skipped: ${result.skipped}`);
      await loadData();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to sync biometric device');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMapping = async () => {
    const validation = validateMapping(mappingForm);
    if (validation) {
      toast.warning(validation);
      return;
    }

    setSaving(true);
    try {
      await biometricApi.saveMapping(mappingForm.employeeId, mappingForm);
      toast.success(editingMapping ? 'Employee biometric mapping updated' : 'Employee biometric mapping saved');
      setMappingDialogOpen(false);
      await loadData();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to save employee biometric mapping');
    } finally {
      setSaving(false);
    }
  };

  const employeeOptions = useMemo(
    () => employees.filter((employee) => employee.isActive).sort((a, b) => a.fullName.localeCompare(b.fullName)),
    [employees]
  );

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2} mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Biometric Module</Typography>
          <Typography variant="body2" color="text.secondary">
            Register biometric devices and map employees with unique device user codes.
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />} onClick={() => void loadData()} disabled={loading} sx={{ textTransform: 'none' }}>
          Refresh
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={3}>
        <Card sx={{ flex: 1 }}><CardContent><Typography color="text.secondary">Devices</Typography><Typography variant="h4">{dashboard?.totalDevices ?? 0}</Typography></CardContent></Card>
        <Card sx={{ flex: 1 }}><CardContent><Typography color="text.secondary">Active Devices</Typography><Typography variant="h4">{dashboard?.activeDevices ?? 0}</Typography></CardContent></Card>
        <Card sx={{ flex: 1 }}><CardContent><Typography color="text.secondary">Employee Mappings</Typography><Typography variant="h4">{dashboard?.totalMappings ?? 0}</Typography></CardContent></Card>
        <Card sx={{ flex: 1 }}><CardContent><Typography color="text.secondary">Logs Today</Typography><Typography variant="h4">{dashboard?.logsToday ?? 0}</Typography></CardContent></Card>
      </Stack>

      <Alert severity="info" sx={{ mb: 3 }}>
        TCP devices use IP and port. Cloud/API providers such as BioMax SmartOffice use tenant-specific URL and API credentials.
      </Alert>

      <Stack direction="row" spacing={1} mb={3}>
        <Button variant={activeTab === 'devices' ? 'contained' : 'outlined'} onClick={() => setActiveTab('devices')} sx={{ textTransform: 'none' }}>
          Devices
        </Button>
        <Button variant={activeTab === 'mappings' ? 'contained' : 'outlined'} onClick={() => setActiveTab('mappings')} sx={{ textTransform: 'none' }}>
          Mappings
        </Button>
        <Button variant={activeTab === 'logs' ? 'contained' : 'outlined'} onClick={() => setActiveTab('logs')} sx={{ textTransform: 'none' }}>
          Logs
        </Button>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : activeTab === 'devices' ? (
        <>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={2}>
            <TextField
              label="Search Devices"
              size="small"
              value={deviceSearch}
              onChange={(e) => setDeviceSearch(e.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleDeviceSearch();
                }
              }}
            />
            <Button variant="outlined" startIcon={<Search />} onClick={handleDeviceSearch} sx={{ textTransform: 'none' }}>
              Search
            </Button>
            {appliedDeviceSearch && (
              <Button variant="text" startIcon={<Clear />} onClick={handleClearDeviceSearch} sx={{ textTransform: 'none' }}>
                Clear
              </Button>
            )}
            <Button variant="contained" onClick={openCreateDevice} sx={{ textTransform: 'none' }}>
              Add Device
            </Button>
          </Stack>
          <TableContainer component={Card}>
            <Table>
             <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Device</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Connection</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Interval</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Mappings</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Last Sync</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {devices.map((device) => (
                  <TableRow key={device.id} hover>
                    <TableCell>
                      <Typography fontWeight={600}>{device.deviceName}</Typography>
                      <Typography variant="caption" color="text.secondary">{device.syncStrategy}</Typography>
                    </TableCell>
                    <TableCell>{device.deviceType}</TableCell>
                    <TableCell>{device.ipAddress || device.apiUrl || device.sdkPath || '-'}</TableCell>
                    <TableCell>{device.pollingInterval} min</TableCell>
                    <TableCell>{device.mappedEmployeeCount}</TableCell>
                    <TableCell>{device.lastSyncAt ? new Date(device.lastSyncAt).toLocaleString() : 'Never'}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Chip size="small" label={device.isActive ? 'Active' : 'Inactive'} color={device.isActive ? 'success' : 'default'} />
                        {device.lastSyncStatus && <Chip size="small" label={device.lastSyncStatus} color={device.lastSyncStatus === 'Success' ? 'success' : device.lastSyncStatus === 'Failed' ? 'error' : 'default'} />}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" startIcon={<Sync />} onClick={() => void handleSyncDevice(device.id)} disabled={saving} sx={{ textTransform: 'none' }}>
                          Sync
                        </Button>
                        <Button size="small" startIcon={<Save />} onClick={() => openEditDevice(device)} disabled={saving} sx={{ textTransform: 'none' }}>
                          Edit
                        </Button>
                        <Button size="small" color="error" startIcon={<Delete />} onClick={() => void handleDeleteDevice(device.id)} disabled={saving} sx={{ textTransform: 'none' }}>
                          Delete
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : activeTab === 'mappings' ? (
        <>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={2}>
            <TextField
              label="Search Mappings"
              size="small"
              value={mappingSearch}
              onChange={(e) => setMappingSearch(e.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleMappingSearch();
                }
              }}
            />
            <Button variant="outlined" startIcon={<Search />} onClick={handleMappingSearch} sx={{ textTransform: 'none' }}>
              Search
            </Button>
            {appliedMappingSearch && (
              <Button variant="text" startIcon={<Clear />} onClick={handleClearMappingSearch} sx={{ textTransform: 'none' }}>
                Clear
              </Button>
            )}
            <Button variant="contained" onClick={() => openEditMapping()} sx={{ textTransform: 'none' }}>
              Add Mapping
            </Button>
          </Stack>
          <TableContainer component={Card}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Employee</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Device</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Device User ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mappings.map((mapping) => (
                  <TableRow key={mapping.id} hover>
                    <TableCell>
                      <Typography fontWeight={600}>{mapping.employeeName}</Typography>
                      <Typography variant="caption" color="text.secondary">{mapping.employeeCode}</Typography>
                    </TableCell>
                    <TableCell>{mapping.deviceName || 'Unassigned'}</TableCell>
                    <TableCell>{mapping.deviceUserId}</TableCell>
                    <TableCell><Chip size="small" label={mapping.isActive ? 'Active' : 'Inactive'} color={mapping.isActive ? 'success' : 'default'} /></TableCell>
                    <TableCell align="right">
                      <Button size="small" startIcon={<Save />} onClick={() => openEditMapping(mapping)} sx={{ textTransform: 'none' }}>
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : (
        <>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={2}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Device</InputLabel>
              <Select
                value={logFilter.biometricDeviceId}
                label="Device"
                onChange={(e) => setLogFilter((prev) => ({ ...prev, biometricDeviceId: e.target.value }))}
              >
                <MenuItem value="">All Devices</MenuItem>
                {devices.map((device) => <MenuItem key={device.id} value={device.id}>{device.deviceName}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField size="small" label="From" type="date" InputLabelProps={{ shrink: true }} value={logFilter.startDate} onChange={(e) => setLogFilter((prev) => ({ ...prev, startDate: e.target.value }))} />
            <TextField size="small" label="To" type="date" InputLabelProps={{ shrink: true }} value={logFilter.endDate} onChange={(e) => setLogFilter((prev) => ({ ...prev, endDate: e.target.value }))} />
            <TextField
              size="small"
              label="Search Logs"
              value={logFilter.searchTerm}
              onChange={(e) => setLogFilter((prev) => ({ ...prev, searchTerm: e.target.value }))}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleLogSearch();
                }
              }}
            />
            <Button variant="outlined" startIcon={<Search />} onClick={handleLogSearch} sx={{ textTransform: 'none' }}>
              Search
            </Button>
            {(appliedLogFilter.biometricDeviceId || appliedLogFilter.startDate || appliedLogFilter.endDate || appliedLogFilter.searchTerm) && (
              <Button variant="text" startIcon={<Clear />} onClick={handleClearLogSearch} sx={{ textTransform: 'none' }}>
                Clear
              </Button>
            )}
          </Stack>
          <TableContainer component={Card}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Employee</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Device</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Punch Time</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Punch Type</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Raw Data</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id} hover>
                    <TableCell>
                      <Typography fontWeight={600}>{log.employeeName}</Typography>
                      <Typography variant="caption" color="text.secondary">{log.employeeCode}</Typography>
                    </TableCell>
                    <TableCell>{log.deviceName || '-'}</TableCell>
                    <TableCell>{new Date(log.punchTime).toLocaleString()}</TableCell>
                    <TableCell>{log.punchType}</TableCell>
                    <TableCell sx={{ maxWidth: 360, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{log.rawData || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Dialog open={deviceDialogOpen} onClose={() => setDeviceDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingDevice ? 'Edit Biometric Device' : 'Add Biometric Device'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Device Name"
              required
              value={deviceForm.deviceName}
              onChange={(e) => setDeviceForm((prev) => ({ ...prev, deviceName: e.target.value }))}
            />
            <FormControl fullWidth>
              <InputLabel>Device Type</InputLabel>
              <Select
                value={deviceForm.deviceType}
                label="Device Type"
                onChange={(e) => {
                  const nextType = e.target.value;
                  setDeviceForm((prev) => ({
                    ...prev,
                    deviceType: nextType,
                    ipAddress: requiresTcpEndpoint(nextType) ? prev.ipAddress : '',
                    port: requiresTcpEndpoint(nextType) ? prev.port : null,
                    apiUrl: isBiomaxDeviceType(nextType) || nextType === 'API' ? prev.apiUrl : '',
                  }));
                  if (isBiomaxDeviceType(nextType)) {
                    setBiomaxConfig(parseBiomaxConfig(deviceForm.providerConfigJson));
                  }
                }}
              >
                <MenuItem value=""><em>Select device type</em></MenuItem>
                <MenuItem value="API">API</MenuItem>
                <MenuItem value="BIOMAX_SMARTOFFICE">BioMax SmartOffice</MenuItem>
                <MenuItem value="SDK">SDK</MenuItem>
                <MenuItem value="ESSL">ESSL</MenuItem>
                <MenuItem value="ZKTeco">ZKTeco</MenuItem>
              </Select>
            </FormControl>
            {requiresTcpSelected && (
              <>
                <TextField
                  label="IP Address"
                  required
                  placeholder="192.168.1.10"
                  value={deviceForm.ipAddress}
                  onChange={(e) => setDeviceForm((prev) => ({ ...prev, ipAddress: e.target.value }))}
                />
                <TextField
                  label="Port"
                  required
                  type="number"
                  value={deviceForm.port ?? ''}
                  onChange={(e) => setDeviceForm((prev) => ({ ...prev, port: e.target.value ? Number(e.target.value) : null }))}
                  inputProps={{ min: 1 }}
                />
              </>
            )}
            {(isBiomaxSelected || selectedDeviceType === 'API') && (
              <>
                <TextField
                  label={isBiomaxSelected ? 'SmartOffice Base URL' : 'API URL'}
                  required
                  placeholder={isBiomaxSelected ? 'http://122.166.44.18:82/' : undefined}
                  value={deviceForm.apiUrl}
                  onChange={(e) => setDeviceForm((prev) => ({ ...prev, apiUrl: e.target.value }))}
                />
                <TextField
                  label="API Key"
                  required={isBiomaxSelected && !editingDevice}
                  type="password"
                  helperText={editingDevice ? 'Leave blank to keep existing key.' : undefined}
                  value={deviceForm.apiKey}
                  onChange={(e) => setDeviceForm((prev) => ({ ...prev, apiKey: e.target.value }))}
                />
              </>
            )}
            {isBiomaxSelected && (
              <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>BioMax SmartOffice Settings</Typography>
                <Stack spacing={2}>
                  <TextField
                    label="Device Serial Numbers"
                    placeholder="Optional, comma separated"
                    helperText="Use when one tenant account has multiple devices and this HRMS device should pull only selected terminals."
                    value={biomaxConfig.serialNumbers}
                    onChange={(e) => setBiomaxConfig((prev) => ({ ...prev, serialNumbers: e.target.value }))}
                  />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      label="Initial Window Days"
                      type="number"
                      value={biomaxConfig.initialWindowDays}
                      onChange={(e) => setBiomaxConfig((prev) => ({ ...prev, initialWindowDays: Number(e.target.value) || 1 }))}
                      inputProps={{ min: 1, max: 90 }}
                      fullWidth
                    />
                    <TextField
                      label="Overlap Days"
                      type="number"
                      value={biomaxConfig.overlapDays}
                      onChange={(e) => setBiomaxConfig((prev) => ({ ...prev, overlapDays: Number(e.target.value) || 0 }))}
                      inputProps={{ min: 0, max: 7 }}
                      fullWidth
                    />
                    <TextField
                      label="Device UTC Offset"
                      type="number"
                      value={biomaxConfig.deviceUtcOffsetMinutes}
                      helperText="IST is 330"
                      onChange={(e) => setBiomaxConfig((prev) => ({ ...prev, deviceUtcOffsetMinutes: e.target.value === '' ? '' : Number(e.target.value) }))}
                      fullWidth
                    />
                  </Stack>
                </Stack>
              </Box>
            )}
            <TextField
              label="Polling Interval (minutes)"
              required
              type="number"
              value={deviceForm.pollingInterval}
              onChange={(e) => setDeviceForm((prev) => ({ ...prev, pollingInterval: Number(e.target.value) || 0 }))}
              inputProps={{ min: 1 }}
            />
            <FormControl fullWidth>
              <InputLabel>Sync Strategy</InputLabel>
              <Select
                value={deviceForm.syncStrategy}
                label="Sync Strategy"
                onChange={(e) => setDeviceForm((prev) => ({ ...prev, syncStrategy: e.target.value }))}
              >
                <MenuItem value="PULL">PULL</MenuItem>
                <MenuItem value="PUSH">PUSH</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel control={<Switch checked={!!deviceForm.isActive} onChange={(e) => setDeviceForm((prev) => ({ ...prev, isActive: e.target.checked }))} />} label="Is Active" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeviceDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={() => void handleSaveDevice()} disabled={saving} sx={{ textTransform: 'none' }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={mappingDialogOpen} onClose={() => setMappingDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingMapping ? 'Edit Employee Mapping' : 'Add Employee Mapping'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Employee</InputLabel>
              <Select
                value={mappingForm.employeeId}
                label="Employee"
                onChange={(e) => setMappingForm((prev) => ({ ...prev, employeeId: e.target.value }))}
                disabled={!!editingMapping}
              >
                {employeeOptions.map((employee) => (
                  <MenuItem key={employee.id} value={employee.id}>{employee.fullName} ({employee.employeeCode})</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Device</InputLabel>
              <Select value={mappingForm.biometricDeviceId || ''} label="Device" onChange={(e) => setMappingForm((prev) => ({ ...prev, biometricDeviceId: e.target.value }))}>
                {devices.map((device) => <MenuItem key={device.id} value={device.id}>{device.deviceName}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField
              label="Device User Code"
              type="number"
              value={mappingForm.deviceUserId}
              onChange={(e) => setMappingForm((prev) => ({ ...prev, deviceUserId: e.target.value }))}
              inputProps={{ min: 1 }}
            />
            <FormControlLabel control={<Switch checked={mappingForm.isActive} onChange={(e) => setMappingForm((prev) => ({ ...prev, isActive: e.target.checked }))} />} label="Is Active" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMappingDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={() => void handleSaveMapping()} disabled={saving} sx={{ textTransform: 'none' }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BiometricManagementPage;
